/**
 * CuttySark 賞（四半期表彰）の予選・本戦セッション API クライアント。
 *
 * 画面からの操作も MCP からの操作も同じ口（product-api の `/cuttysark`）に入る。**判定は
 * API 側の 1 本**（domain の cuttysark-rules）にあるので、ここには票数・点数の上限や
 * 進行の遷移条件を写さない。弾かれたときは `ApiError.message` をそのまま出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの一覧・作成・設定・進行・投票を行う API クライアント
 * @graph-connects /cuttysark [calls] CuttySark 賞セッション API
 */

import type {
  ControlCuttysarkSessionRequest,
  CreateCuttysarkSessionRequest,
  CuttysarkBallot,
  CuttysarkLiveView,
  CuttysarkMyCommentsResponse,
  CuttysarkSessionListResponse,
  PutCuttysarkBallotRequest,
  PutCuttysarkCandidatePhotoRequest,
  UpdateCuttysarkSessionRequest,
} from '@cortex/domain-product-innovation-lab-types';
import { request } from './_request.js';

/** セッション配下のパスを組み立てる。 @graph-connects none */
function sessionPath(sessionId: string, suffix = ''): string {
  return `/cuttysark/sessions/${encodeURIComponent(sessionId)}${suffix}`;
}

/** CuttySark 賞セッション API。 @graph-connects none */
export const cuttysarkApi = {
  /** @graph-connects /cuttysark/sessions [calls] セッション一覧の取得 */
  list: (): Promise<CuttysarkSessionListResponse> => request('/cuttysark/sessions'),

  /** @graph-connects /cuttysark/sessions [calls] セッションの作成 */
  create: (input: CreateCuttysarkSessionRequest): Promise<CuttysarkLiveView> =>
    request('/cuttysark/sessions', { method: 'POST', body: JSON.stringify(input) }),

  /** @graph-connects /cuttysark/sessions/{sessionId}/live [calls] 閲覧者ごとの進行状態の取得 */
  live: (sessionId: string): Promise<CuttysarkLiveView> => request(sessionPath(sessionId, '/live')),

  /** @graph-connects /cuttysark/sessions/{sessionId} [calls] 部門・候補者・参加者の設定更新 */
  update: (sessionId: string, input: UpdateCuttysarkSessionRequest): Promise<CuttysarkLiveView> =>
    request(sessionPath(sessionId), { method: 'PATCH', body: JSON.stringify(input) }),

  /** @graph-connects /cuttysark/sessions/{sessionId}/control [calls] 進行操作 */
  control: (sessionId: string, input: ControlCuttysarkSessionRequest): Promise<CuttysarkLiveView> =>
    request(sessionPath(sessionId, '/control'), { method: 'POST', body: JSON.stringify(input) }),

  /** @graph-connects /cuttysark/sessions/{sessionId}/ballot/me [calls] 自分の投票の取得 */
  myBallot: (sessionId: string): Promise<{ ballot: CuttysarkBallot | null }> =>
    request(sessionPath(sessionId, '/ballot/me')),

  /** @graph-connects /cuttysark/sessions/{sessionId}/comments/me [calls] 自分への応援コメントの取得 */
  myComments: (sessionId: string): Promise<CuttysarkMyCommentsResponse> =>
    request(sessionPath(sessionId, '/comments/me')),

  /** @graph-connects /cuttysark/sessions/{sessionId}/ballot/me [calls] 自分の投票の送信（全置換） */
  submitBallot: (
    sessionId: string,
    input: PutCuttysarkBallotRequest,
  ): Promise<{ ballot: CuttysarkBallot }> =>
    request(sessionPath(sessionId, '/ballot/me'), { method: 'PUT', body: JSON.stringify(input) }),

  /** @graph-connects /cuttysark/sessions/{sessionId}/candidates/{email}/photo [calls] 候補者の写真のアップロード */
  uploadPhoto: (
    sessionId: string,
    email: string,
    input: PutCuttysarkCandidatePhotoRequest,
  ): Promise<CuttysarkLiveView> =>
    request(sessionPath(sessionId, `/candidates/${encodeURIComponent(email)}/photo`), {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  /** @graph-connects /cuttysark/sessions/{sessionId}/candidates/{email}/photo [calls] 候補者の写真の削除 */
  deletePhoto: (sessionId: string, email: string): Promise<CuttysarkLiveView> =>
    request(sessionPath(sessionId, `/candidates/${encodeURIComponent(email)}/photo`), {
      method: 'DELETE',
    }),

  /** @graph-connects /cuttysark/sessions/{sessionId}/ballots [calls] 運営向けの投票明細の取得 */
  ballots: (sessionId: string): Promise<{ ballots: CuttysarkBallot[] }> =>
    request(sessionPath(sessionId, '/ballots')),
};
